import { useEffect } from 'react';

/** 移動超過這個距離(px)才算「拖拉」,以下都當成點擊 —— 手在滑鼠上本來就會微微抖 */
const DRAG_THRESHOLD = 5;

/**
 * 慣性的每幀衰減率(以 60fps 為基準),越小停得越快。
 * 0.92 大約對應 0.7 秒內停下 —— 0.94 實測會拖到 1.2 秒,滑起來會有「煞不住」的感覺。
 */
const FRICTION = 0.92;

/**
 * 速度低於這個值(px/ms)就收手。
 * 0.05 px/ms ≈ 每幀 0.8px,再慢下去肉眼看不出還在動,卻還在燒 rAF。
 */
const MIN_VELOCITY = 0.05;

/**
 * 初速上限(px/ms)。滑鼠事件偶爾會擠在一起送達(視窗剛回到前景、系統忙碌),
 * 算出來的瞬時速度會失真地大,不夾住的話一甩就飛到底。
 */
const MAX_VELOCITY = 3;

/** 只採計最近這段時間內的移動來估算速度,避免「拖到一半停住再放開」也飛出去 */
const VELOCITY_WINDOW_MS = 100;

/**
 * 讓原生橫向捲動容器可以用滑鼠直接拖拉,放開後帶慣性滑行。
 *
 * 搭配 useHorizontalWheelScroll 使用,兩者共用同一個 ref:
 *
 *   const scrollRef = useHorizontalWheelScroll();
 *   useDragScroll(scrollRef);
 *
 * ── 幾個刻意的決定 ──
 *
 * **只接滑鼠,不碰觸控。** 手機的原生橫向捲動(含慣性)瀏覽器做得比我們好,
 * 插手只會讓手感變差,所以 pointerType 是 touch 就直接放行。
 *
 * **不用 setPointerCapture。** 一旦對容器設了 pointer capture,pointerup 的
 * target 會變成容器,click 就改派到容器身上 —— 卡片的 onClick 會完全收不到,
 * 變成「點了進不了遊戲」。改成拖曳期間把 pointermove / pointerup 掛到 window,
 * 點擊的派發路徑維持原樣。
 *
 * **拖過的那一次點擊要吃掉。** 卡片本身有 onClick(進遊戲),
 * 不擋的話每拖一次就誤入一款遊戲。用 capture 階段攔截,搶在卡片的 handler 之前。
 *
 * @param {React.RefObject<HTMLElement>} ref 目標容器(overflowX 為 auto/scroll)
 * @param {object}  [options]
 * @param {boolean} [options.inertia=true] 放開後是否慣性滑行
 */
const useDragScroll = (ref, { inertia = true } = {}) => {
  useEffect(() => {
    const el = ref?.current;
    if (!el) return;

    const prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const useInertia = inertia && !prefersReducedMotion;

    let isDragging = false;
    let didDrag = false; // 這一輪有沒有真的移動到門檻以上
    let startX = 0;
    let startScrollLeft = 0;
    let samples = []; // [{ x, t }],用來估算放開瞬間的速度
    let rafId = null;

    const stopInertia = () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const runInertia = (velocity) => {
      // velocity 單位是 px/ms,正值代表指標往右移(內容應該往左捲)
      let v = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, velocity));
      let last = window.performance.now();

      const step = (now) => {
        const dt = Math.min(now - last, 50); // 分頁切回來時 dt 會很大,夾住免得暴衝
        last = now;

        const before = el.scrollLeft;
        el.scrollLeft -= v * dt;

        // 撞到最左/最右就停,不要空轉
        if (el.scrollLeft === before) {
          rafId = null;
          return;
        }

        v *= Math.pow(FRICTION, dt / 16.67);
        if (Math.abs(v) < MIN_VELOCITY) {
          rafId = null;
          return;
        }
        rafId = window.requestAnimationFrame(step);
      };

      rafId = window.requestAnimationFrame(step);
    };

    const onPointerDown = (e) => {
      if (e.pointerType === 'touch') return; // 觸控交給瀏覽器原生
      if (e.button !== 0) return; // 只認左鍵
      if (el.scrollWidth <= el.clientWidth) return; // 沒得捲

      stopInertia();

      isDragging = true;
      didDrag = false;
      startX = e.clientX;
      startScrollLeft = el.scrollLeft;
      samples = [{ x: e.clientX, t: e.timeStamp }];

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    };

    const onPointerMove = (e) => {
      if (!isDragging) return;

      const dx = e.clientX - startX;

      if (!didDrag && Math.abs(dx) >= DRAG_THRESHOLD) {
        didDrag = true;
        // 進入拖曳後才改游標與停用選取,單純點擊時不要動使用者的畫面
        document.body.style.cursor = 'grabbing';
        el.style.userSelect = 'none';
      }
      if (!didDrag) return;

      // 指標往右 → 內容往左捲
      el.scrollLeft = startScrollLeft - dx;

      samples.push({ x: e.clientX, t: e.timeStamp });
      const cutoff = e.timeStamp - VELOCITY_WINDOW_MS;
      while (samples.length > 2 && samples[0].t < cutoff) samples.shift();
    };

    const onPointerUp = () => {
      if (!isDragging) return;
      isDragging = false;

      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);

      document.body.style.cursor = '';
      el.style.userSelect = '';

      if (!didDrag) return;

      if (useInertia && samples.length >= 2) {
        const first = samples[0];
        const last = samples[samples.length - 1];
        const dt = last.t - first.t;
        if (dt > 0) {
          const v = (last.x - first.x) / dt;
          if (Math.abs(v) >= MIN_VELOCITY) runInertia(v);
        }
      }
      samples = [];
    };

    /**
     * 吃掉「拖曳結束後緊接著發生的那一次 click」。
     * 用 capture 階段是為了搶在卡片自己的 onClick 之前;
     * React 的合成事件掛在 root,這裡在容器攔下就傳不上去了。
     */
    const onClickCapture = (e) => {
      if (!didDrag) return;
      didDrag = false;
      e.preventDefault();
      e.stopPropagation();
    };

    /** 卡片裡有 <img>,不擋的話按住會變成瀏覽器原生的「拖曳圖片」 */
    const onDragStart = (e) => e.preventDefault();

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('click', onClickCapture, true);
    el.addEventListener('dragstart', onDragStart);
    // 用滾輪時把慣性收掉,兩者同時改 scrollLeft 會互相打架
    el.addEventListener('wheel', stopInertia, { passive: true });

    return () => {
      stopInertia();
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('click', onClickCapture, true);
      el.removeEventListener('dragstart', onDragStart);
      el.removeEventListener('wheel', stopInertia);
      document.body.style.cursor = '';
      el.style.userSelect = '';
    };
  }, [ref, inertia]);
};

export default useDragScroll;
